import { postForm, type FormFields } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { getListRows } from '@/shared/lib/pagination';

type Response = Record<string, unknown>;

/** Review status of a verification section (legacy `request-status.json`). */
export const SECTION_STATUS = { NEW: 0, WAITING: 1, REJECT: 2, VERIFY: 3 } as const;

/** Section names accepted by `set-section-status`. */
export type SectionName = 'contacts' | 'specialization' | 'legal_data' | 'photo_video';

export interface SectionStatus {
  status_id?: number;
  verifier?: { name?: string } | null;
}

export interface VerificationRequest extends Record<string, unknown> {
  id: number;
  create_time?: number;
  status_id?: number;
  city?: string;
  service: { id: number | string; name?: string };
  service_operator?: { name?: string } | null;
  contacts: SectionStatus;
  specialization: SectionStatus;
  legal_data: SectionStatus;
  photo_video: SectionStatus;
  errors?: Record<string, string[] | string>;
}

export interface VerifyListParams {
  page: number;
  verified_only: number;
  request_id: string;
  service_id: string;
  city_id: string;
  service_operator_id: string;
}

export const fetchVerifyList = (params: VerifyListParams) =>
  postForm<Response>('/service/verification/get-requests-list/', { ...params });

export async function fetchManagers() {
  const res = await postForm<Response>('/user/profile/list/', { roles: ['sales-manager'] });
  return getListRows<{ id: number; name?: string }>(res, ['users']);
}

export const verifyRequest = (requestId: number) =>
  submitForm('/service/verification/verify-service/', { request_id: requestId });

export const setSectionStatus = (v: {
  requestId: number;
  section: SectionName;
  statusId: number;
  rejectMessage?: string;
}) =>
  submitForm('/service/verification/set-section-status/', {
    request_id: v.requestId,
    section: v.section,
    status_id: v.statusId,
    reject_message: v.rejectMessage,
  });

// ---- section data ----------------------------------------------------------

export interface SectionData extends Record<string, unknown> {
  status?: number;
  reject_message?: string;
}

export interface SchedulePeriod {
  is_active?: number | boolean;
  all_day?: number | boolean;
  from?: string;
  to?: string;
}

export type ScheduleKey = 'weekdays' | 'weekends' | 'holidays';

export const EMPTY_PERIOD: SchedulePeriod = { is_active: 0, all_day: 0, from: '', to: '' };

export interface ContactsData extends SectionData {
  name?: string;
  phone?: string;
  email?: string;
  description?: string;
  address?: string;
  country_id?: number | string;
  region_id?: number | string;
  city_id?: number | string;
  lat?: number | string;
  long?: number | string;
  schedule?: Partial<Record<ScheduleKey, SchedulePeriod>> | null;
}

export interface VerificationFile {
  id: number;
  name: string;
  url: string;
}

export interface LegalData extends SectionData {
  contract?: VerificationFile[] | null;
  entity_card?: Record<string, string> | null;
}

export interface SpecData extends SectionData {
  auto_marks?: { mark_name: string }[] | null;
  work_types?: { name: string }[] | null;
}

export interface MediaItem {
  id?: number;
  url?: string;
  src?: string;
  thumb_200_200?: string;
}

export interface PhotoData extends SectionData {
  logo?: MediaItem[] | null;
  entrance_area?: MediaItem[] | null;
  waiting_area?: MediaItem[] | null;
  acceptance_area?: MediaItem[] | null;
  repair_area?: MediaItem[] | null;
  video?: MediaItem[] | null;
}

async function fetchSection<T>(url: string, key: string, requestId: number) {
  const res = await postForm<Response>(url, { request_id: requestId });
  return (res[key] ?? {}) as T;
}

export const fetchContacts = (id: number) =>
  fetchSection<ContactsData>('/service/verification/get-contacts/', 'contacts', id);
export const fetchLegal = (id: number) =>
  fetchSection<LegalData>('/service/verification/get-legal-data/', 'legal_data', id);
export const fetchSpec = (id: number) =>
  fetchSection<SpecData>('/service/verification/get-specialization/', 'specialization', id);
export const fetchPhoto = (id: number) =>
  fetchSection<PhotoData>('/service/verification/get-photo-video/', 'photo_video', id);

// ---- section editing -------------------------------------------------------

export interface ContactsFormValues {
  name: string;
  phone: string;
  email: string;
  description: string;
  address: string;
  countryId: string;
  regionId: string;
  cityId: string;
  lat: string;
  long: string;
  schedule: Record<ScheduleKey, SchedulePeriod>;
}

const SCHEDULE_KEYS: ScheduleKey[] = ['weekdays', 'weekends', 'holidays'];

export function editContacts(requestId: number, v: ContactsFormValues) {
  const fields: FormFields = {
    request_id: requestId,
    'contacts[name]': v.name,
    'contacts[phone]': v.phone,
    'contacts[email]': v.email,
    'contacts[description]': v.description,
    'contacts[address]': v.address,
    'contacts[country_id]': v.countryId,
    'contacts[region_id]': v.regionId,
    'contacts[city_id]': v.cityId,
    'contacts[lat]': v.lat,
    'contacts[long]': v.long,
  };
  SCHEDULE_KEYS.forEach((key) => {
    const period = v.schedule[key];
    const prefix = `contacts[schedule][${key}]`;
    fields[`${prefix}[is_active]`] = Number(period.is_active);
    fields[`${prefix}[all_day]`] = Number(period.all_day);
    fields[`${prefix}[from]`] = period.from;
    fields[`${prefix}[to]`] = period.to;
  });
  return submitForm('/service/verification/edit-contacts-section/', fields);
}

export function editLegal(requestId: number, entityCard: Record<string, string>) {
  const fields: FormFields = { request_id: requestId };
  Object.entries(entityCard).forEach(([key, value]) => {
    fields[`entity_card[${key}]`] = value;
  });
  return submitForm('/service/verification/edit-legal-data-section/', fields);
}

export const deleteVerificationFile = (id: number) =>
  submitForm('/service/profile/delete-file/', { id });
